<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Add Withdrawal Log
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.user_id"
                  :items="users"
                  item-text="text"
                  item-value="value"
                  outlined
                  label="User *"
                  prepend-inner-icon="mdi-account"
                  :error-messages="errors.user_id"
                  :loading="loadingUsers"
                  @change="loadBankDetails"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.bank_detail_id"
                  :items="bankDetails"
                  item-text="text"
                  item-value="value"
                  outlined
                  label="Bank Details"
                  prepend-inner-icon="mdi-bank"
                  :error-messages="errors.bank_detail_id"
                  :loading="loadingBankDetails"
                  :disabled="!form.user_id"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.transfer_id"
                  outlined
                  label="Transfer ID *"
                  prepend-inner-icon="mdi-identifier"
                  :error-messages="errors.transfer_id"
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.transfer_amount"
                  outlined
                  label="Transfer Amount (in Paise) *"
                  prepend-inner-icon="mdi-currency-inr"
                  :error-messages="errors.transfer_amount"
                  type="number"
                  hint="Enter amount in paise (e.g., 150000 for ₹1500)"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.transfer_mode"
                  :items="transferModeOptions"
                  outlined
                  label="Transfer Mode"
                  prepend-inner-icon="mdi-bank-transfer"
                  :error-messages="errors.transfer_mode"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.fundsource_id"
                  outlined
                  label="Fund Source ID"
                  prepend-inner-icon="mdi-source-branch"
                  :error-messages="errors.fundsource_id"
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.status"
                  :items="statusOptions"
                  outlined
                  label="Status"
                  prepend-inner-icon="mdi-toggle-switch"
                  :error-messages="errors.status"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.status_code"
                  outlined
                  label="Status Code"
                  prepend-inner-icon="mdi-numeric"
                  :error-messages="errors.status_code"
                ></v-text-field>
              </v-col>
              <v-col sm="12">
                <v-textarea
                  v-model="form.status_description"
                  outlined
                  label="Status Description"
                  prepend-inner-icon="mdi-text"
                  :error-messages="errors.status_description"
                  rows="3"
                ></v-textarea>
              </v-col>
              <v-col sm="12">
                <v-textarea
                  v-model="form.remarks"
                  outlined
                  label="Remarks"
                  prepend-inner-icon="mdi-comment-text"
                  :error-messages="errors.remarks"
                  rows="3"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="save"
            :loading="loading"
            :disabled="!valid || loading"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      valid: false,
      loading: false,
      loadingUsers: false,
      loadingBankDetails: false,
      users: [],
      bankDetails: [],
      form: {
        user_id: null,
        bank_detail_id: null,
        transfer_id: null,
        transfer_amount: null,
        transfer_mode: null,
        fundsource_id: null,
        status: "PENDING",
        status_code: null,
        status_description: null,
        remarks: null,
      },
      transferModeOptions: [
        { text: "Bank Transfer", value: "banktransfer" },
        { text: "RTGS", value: "rtgs" },
        { text: "NEFT", value: "neft" },
        { text: "IMPS", value: "imps" },
        { text: "UPI", value: "upi" },
      ],
      statusOptions: [
        { text: "Pending", value: "PENDING" },
        { text: "Success", value: "SUCCESS" },
        { text: "Failed", value: "FAILED" },
        { text: "Cancelled", value: "CANCELLED" },
        { text: "Processing", value: "PROCESSING" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      errors: "errors/errors",
    }),
  },
  mounted() {
    this.loadUsers();
    this.generateTransferId();
  },
  methods: {
    async loadUsers() {
      this.loadingUsers = true;
      try {
        const response = await axios.get("/users");
        this.users = response.data.data.map((user) => ({
          text: `${user.first_name} ${user.last_name} (${user.phone})`,
          value: user.id,
        }));
      } catch (error) {
        console.error("Error loading users:", error);
        this.$store.dispatch("errors/setError", error);
      } finally {
        this.loadingUsers = false;
      }
    },

    async loadBankDetails() {
      if (!this.form.user_id) {
        this.bankDetails = [];
        this.form.bank_detail_id = null;
        return;
      }

      this.loadingBankDetails = true;
      try {
        const response = await axios.get("/bank_details", {
          params: { user_id: this.form.user_id },
        });
        this.bankDetails = response.data.data.map((bank) => ({
          text: `${bank.name} - ${bank.account_number} (${bank.bank_name})`,
          value: bank.id,
        }));
      } catch (error) {
        console.error("Error loading bank details:", error);
        this.bankDetails = [];
        this.$store.dispatch("errors/setError", error);
      } finally {
        this.loadingBankDetails = false;
      }
    },

    generateTransferId() {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 10000);
      this.form.transfer_id = `TRANSFER_${timestamp}_${random}`;
    },

    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      try {
        const response = await axios.post("/withdrawal_logs", this.form);
        
        if (response.data.success) {
          this.$toast.success(response.data.sub_title || "Withdrawal log created successfully");
          this.$router.push("/withdrawal_logs");
        } else {
          this.$toast.error(response.data.sub_title || "Error creating withdrawal log");
        }
      } catch (error) {
        console.error("Error saving withdrawal log:", error);
        this.$store.dispatch("errors/setError", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>